import React from 'react';
import {GraphLayout, GraphViewKind} from '../../../../../components/graph/interface';
import {getLayoutDiff} from '../../../../../components/graph/helpers';
import {useLayoutAndCameraProps} from './use-layout-and-camera-props';
import {useActionHistory} from './use-action-history';
import {usePresentationEditedProps} from './use-presentation-edited-props';
import {QueryGraphLayoutAlgorithm} from '../../../../../components/graph/cytoscape-graph/interface';
import {useGraphSettings} from './use-graph-settings';
import {MaxPathLengthOptionResult} from './requests/use-query-result-max-length-options';
import {useGraphSelectionProps} from '../../../../../components/graph/use-graph-selection-props';
import {useResultsListProps} from '../result-chains-panel/use-results-list-props';

interface Props {
    actionHistoryProps: ReturnType<typeof useActionHistory>;
    layoutAndCameraProps: ReturnType<typeof useLayoutAndCameraProps>;
    presentationEditedProps: ReturnType<typeof usePresentationEditedProps>;
    graphSettings: ReturnType<typeof useGraphSettings>;
    maxPathLengthOptions: MaxPathLengthOptionResult;
    selectionProps: ReturnType<typeof useGraphSelectionProps>;
    resultsListProps: ReturnType<typeof useResultsListProps>;
}

export function useQueryGraphActions({maxPathLengthOptions, ...restProps}: Props) {
    const {
        isCustomLayout,
        setIsCustomLayout,
        setVisibleNodesPositions,
        visibleNodesPositions,
        layoutAlgorithm,
        setLayoutAlgorithm,
        clearVisibleNodesPositions,
        calculateLayout
    } = restProps.layoutAndCameraProps;
    const {addToHistory} = restProps.actionHistoryProps;
    const {isPresentationEdited, handlePresentationEdited} = restProps.presentationEditedProps;
    const {maxPathLength, setMaxPathLength, viewKind, setViewKind} = restProps.graphSettings;
    const {selectedNodes, onNodesSelected} = restProps.selectionProps;
    const {selectedResultItem, setSelectedResultItem} = restProps.resultsListProps;
    const visibleNodesPositionsRef = React.useRef(visibleNodesPositions);
    visibleNodesPositionsRef.current = visibleNodesPositions;

    const nodeMovedAction = React.useCallback(
        (newLayout: GraphLayout, isRedoAction?: boolean) => {
            setIsCustomLayout('true');
            setVisibleNodesPositions(JSON.stringify(newLayout));
            handlePresentationEdited();

            if (!isRedoAction) {
                const {changedPositionsNew, changedPositionsOld} = getLayoutDiff(
                    visibleNodesPositionsRef.current!,
                    newLayout
                );
                addToHistory([
                    {
                        actionType: 'MOVED_NODES',
                        oldCoordinates: changedPositionsOld,
                        newCoordinates: changedPositionsNew,
                        isEditingPresentationStarted: !isPresentationEdited,
                        isEditingLayoutStarted: !isCustomLayout
                    }
                ]);
            }
        },
        [
            setIsCustomLayout,
            setVisibleNodesPositions,
            handlePresentationEdited,
            addToHistory,
            isPresentationEdited,
            isCustomLayout
        ]
    );

    const rebuildLayoutAction = React.useCallback(
        (newAlgorithm: QueryGraphLayoutAlgorithm, isRedoAction?: boolean) => {
            setLayoutAlgorithm(newAlgorithm);
            handlePresentationEdited();
            calculateLayout();
            clearVisibleNodesPositions();

            if (!isRedoAction) {
                addToHistory([
                    {
                        actionType: 'REBUILD_LAYOUT',
                        newAlgorithm,
                        oldAlgorithm: layoutAlgorithm,
                        isEditingPresentationStarted: !isPresentationEdited,
                        oldLayoutWasCustom: isCustomLayout
                    }
                ]);
            }
        },
        [
            setLayoutAlgorithm,
            handlePresentationEdited,
            calculateLayout,
            addToHistory,
            layoutAlgorithm,
            isPresentationEdited,
            isCustomLayout,
            clearVisibleNodesPositions
        ]
    );

    const maxPathLengthSelectedAction = React.useCallback(
        (newValue: number, isRedoAction?: boolean) => {
            setMaxPathLength(String(newValue));
            handlePresentationEdited();
            clearVisibleNodesPositions();

            if (!isRedoAction) {
                addToHistory([
                    {
                        actionType: 'SETTINGS_CHANGED',
                        newMaxPathLength: newValue,
                        oldMaxPathLength: maxPathLength,
                        isEditingPresentationStarted: !isPresentationEdited,
                        oldLayout: visibleNodesPositionsRef.current!,
                        oldViewKind: viewKind,
                        newViewKind: viewKind
                    }
                ]);
            }
        },
        [
            setMaxPathLength,
            handlePresentationEdited,
            clearVisibleNodesPositions,
            maxPathLength,
            addToHistory,
            isPresentationEdited,
            viewKind
        ]
    );

    const viewKindToggledAction = React.useCallback(
        (isRedoAction?: boolean) => {
            const newViewKind =
                viewKind === GraphViewKind.MERGED ? GraphViewKind.IDENTICAL : GraphViewKind.MERGED;

            let newMaxPathLength = Number(maxPathLength);
            // sometimes max path length options can be different for different view kinds,
            // in that case select the closest lesser corresponding max path length option
            while (
                !maxPathLengthOptions[newViewKind].find(
                    // eslint-disable-next-line no-loop-func
                    (option) => option.pathLength === newMaxPathLength
                )
            ) {
                newMaxPathLength--;
            }

            if (newMaxPathLength !== Number(maxPathLength)) {
                setMaxPathLength(String(newMaxPathLength));
            }

            setViewKind(newViewKind);
            handlePresentationEdited();
            clearVisibleNodesPositions();

            if (!isRedoAction) {
                addToHistory([
                    {
                        actionType: 'SETTINGS_CHANGED',
                        newMaxPathLength,
                        oldMaxPathLength: maxPathLength,
                        isEditingPresentationStarted: !isPresentationEdited,
                        oldLayout: visibleNodesPositionsRef.current!,
                        oldViewKind: viewKind,
                        newViewKind
                    }
                ]);
            }
        },
        [
            isPresentationEdited,
            setViewKind,
            handlePresentationEdited,
            viewKind,
            maxPathLength,
            setMaxPathLength,
            maxPathLengthOptions,
            clearVisibleNodesPositions,
            addToHistory
        ]
    );

    const nodeSelectionChangedAction = React.useCallback(
        (value: number[], isSelectMultiple: boolean, isRedoAction?: boolean) => {
            const newSelection = onNodesSelected(value, isSelectMultiple);
            setSelectedResultItem(undefined);

            if (!isRedoAction) {
                addToHistory([
                    {
                        actionType: 'SELECTION_CHANGED',
                        newSelection,
                        oldSelection: selectedNodes,
                        oldSelectedListResultItem: selectedResultItem
                    }
                ]);
            }
        },
        [onNodesSelected, selectedNodes, addToHistory, setSelectedResultItem, selectedResultItem]
    );

    const clearSelectionAction = React.useCallback(
        () => nodeSelectionChangedAction([], false),
        [nodeSelectionChangedAction]
    );

    const listResultSelectedAction = React.useCallback(
        (item: number | undefined, isRedoAction?: boolean) => {
            onNodesSelected([], false);
            setSelectedResultItem(item);
            if (!isRedoAction) {
                addToHistory([
                    {
                        actionType: 'LIST_RESULT_SELECTED',
                        newSelectedListResultItem: item,
                        oldSelection: selectedNodes,
                        oldSelectedListResultItem: selectedResultItem
                    }
                ]);
            }
        },
        [onNodesSelected, addToHistory, setSelectedResultItem, selectedResultItem, selectedNodes]
    );

    const clearListResultSelectionAction = React.useCallback(
        () => listResultSelectedAction(undefined),
        [listResultSelectedAction]
    );

    const selectionChangedEffect = React.useCallback(
        (newSelection: number[]) => {
            addToHistory([
                {
                    effectType: 'SELECTION_CHANGED',
                    newSelection,
                    oldSelection: selectedNodes
                }
            ]);
        },
        [addToHistory, selectedNodes]
    );

    return {
        nodeMovedAction,
        rebuildLayoutAction,
        maxPathLengthSelectedAction,
        viewKindToggledAction,
        nodeSelectionChangedAction,
        clearSelectionAction,
        selectionChangedEffect,
        listResultSelectedAction,
        clearListResultSelectionAction
    };
}
